import { compressableFileTypes, Document } from '@sc/data-access-models';
import { EMPTY, Observable, of } from 'rxjs';
import { expand, takeLast } from 'rxjs/operators';

export const MAX_OCTET_FILE = 5_242_880; // 5 Mo
export const MAX_OCTET_PICTURE = 2_097_152; // 2 Mo

export function isValidFileType(file: File, mimeTypeList: string[]) {
  return mimeTypeList.includes(file.type);
}

export function isValidDocType(doc: Document, mimeTypeList: string[]) {
  return mimeTypeList.includes(doc.metadata.mimeType);
}

export function loadFile(file: File): Observable<string> {
  const reader: FileReader = new FileReader();
  reader.readAsDataURL(file);

  return new Observable((observer) => {
    reader.onload = (ev) => {
      observer.next(reader.result?.toString());
    };

    reader.onerror = (error) => {
      observer.error(error);
    };
  });
}

export const compressFile = (file: File): Observable<File> => {
  if (
    compressableFileTypes.includes(file.type) &&
    file.size > MAX_OCTET_PICTURE
  ) {
    const query$ = compress(file);

    return query$.pipe(
      expand((_file) => {
        return _file.size <= MAX_OCTET_PICTURE ? EMPTY : compress(_file);
      }),
      takeLast(1)
    );
  } else {
    return of(file);
  }
};

const compress = (file: File): Observable<File> => {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  return new Observable((observer) => {
    reader.onload = (ev) => {
      const img = new Image();
      img.src = (ev.target as any).result;
      (img.onload = () => {
        const elem = document.createElement('canvas'); // Use Angular's Renderer2 method
        elem.width = img.width / 2;
        elem.height = img.height / 2;
        const ctx = elem.getContext('2d') as CanvasRenderingContext2D;
        ctx.drawImage(img, 0, 0, elem.width, elem.height);
        ctx.canvas.toBlob(
          (blob) => {
            const newFile = new File([blob], file.name, {
              type: file.type ?? 'image/png',
              lastModified: Date.now(),
            });

            observer.next(newFile);
            observer.complete();
          },
          file.type ?? 'image/png',
          1
        );
      }),
        (reader.onerror = (error) => {
          observer.error(error);
        });
    };
  });
};
